/**
 * Per-session export bundle modal — Phase 1B-K (2026-05-30).
 *
 * Operator clicks "Export bundle" in the SessionPage sidebar; the
 * modal POSTs to /v1/sessions/<id>/export which queues a Celery task,
 * then polls /v1/sessions/<id>/exports/<exportId> until status flips
 * to ready (download URL appears) or failed (error string).
 *
 * Also lists every prior export so the operator can re-download an
 * older bundle without re-running the job.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime, formatTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";


type ExportRow = {
  id: string;
  session_id: string;
  status: "queued" | "processing" | "ready" | "failed";
  byte_size: number;
  sha256: string;
  requested_at: string;
  completed_at: string | null;
  error: string | null;
  file_count: number;
  download_url?: string | null;
};


export function ExportBundleModal({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["session-exports", sessionId],
    queryFn: async () => {
      const res = await api.get<{ results: ExportRow[] }>(
        ApiPaths.sessionExportList(sessionId),
      );
      return res.data.results;
    },
    refetchInterval: 4000,
  });

  // Detail poll for the currently-tracked export so we get the
  // download_url the moment status flips to ready.
  const detail = useQuery({
    queryKey: ["session-export-detail", sessionId, activeId],
    enabled: !!activeId,
    queryFn: async () => {
      const res = await api.get<ExportRow>(
        ApiPaths.sessionExportDetail(sessionId, activeId!),
      );
      return res.data;
    },
    refetchInterval: (q) => {
      const row = q.state.data as ExportRow | undefined;
      if (!row) return 2000;
      if (row.status === "queued" || row.status === "processing") return 2000;
      return false;
    },
  });

  const queueNew = useMutation({
    mutationFn: async () => {
      const res = await api.post<ExportRow>(
        ApiPaths.sessionExportRequest(sessionId),
      );
      return res.data;
    },
    onSuccess: (row) => {
      setActiveId(row.id);
      void qc.invalidateQueries({
        queryKey: ["session-exports", sessionId],
      });
    },
  });

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tracked = activeId
    ? (detail.data ?? list.data?.find((r) => r.id === activeId) ?? null)
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.exportBundleModal.dialogLabel")}
      className="modal-backdrop bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-nb-border bg-nb-bg p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-nb-text">
              {t("ui.exportBundleModal.title")}
            </h2>
            <p className="mt-1 text-xs text-nb-text-2">
              {t("ui.exportBundleModal.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-nb-text-3 hover:bg-nb-surface"
            aria-label={t("ui.exportBundleModal.close")}
          >
            ✕
          </button>
        </header>

        {/* Active job — what was just queued. */}
        {tracked && (
          <ActiveJob row={tracked} />
        )}

        {/* CTA */}
        <button
          type="button"
          className="btn-primary mt-4 w-full"
          onClick={() => queueNew.mutate()}
          disabled={queueNew.isPending}
          data-testid="export-queue-new"
        >
          {queueNew.isPending
            ? t("ui.exportBundleModal.queuing")
            : tracked
              ? t("ui.exportBundleModal.buildNew")
              : t("ui.exportBundleModal.build")}
        </button>
        {queueNew.isError && (
          <p role="alert" className="mt-2 text-xs text-cvp-danger">
            {(queueNew.error as Error).message}
          </p>
        )}

        {/* History */}
        {list.data && list.data.length > 0 && (
          <section className="mt-5 border-t border-nb-border pt-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-nb-text-2">
              {t("ui.exportBundleModal.pastBundles")}
            </h3>
            <ul className="space-y-1.5">
              {list.data
                .filter((r) => r.id !== activeId)
                .map((r) => (
                  <ExportRowItem
                    key={r.id}
                    row={r}
                    onTrack={() => setActiveId(r.id)}
                  />
                ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}


function ActiveJob({ row }: { row: ExportRow }) {
  const { t } = useTranslation();
  const pct =
    row.status === "ready"
      ? 100
      : row.status === "failed"
        ? 100
        : row.status === "processing"
          ? 65
          : 25;
  return (
    <div
      className="rounded-xl border border-nb-border bg-nb-surface/40 p-3"
      data-testid="export-active-job"
      data-status={row.status}
    >
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-nb-text">
          {row.status === "ready"
            ? t("ui.exportBundleModal.statusReady")
            : row.status === "failed"
              ? t("ui.exportBundleModal.statusFailed")
              : row.status === "processing"
                ? t("ui.exportBundleModal.statusBuilding")
                : t("ui.exportBundleModal.statusQueued")}
        </span>
        <span className="text-nb-text-3">
          {formatTime(row.requested_at)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-nb-bg">
        <div
          className={`h-full transition-all duration-500 ${
            row.status === "failed" ? "bg-cvp-danger" : "bg-nb-mint"
          } ${row.status === "queued" || row.status === "processing" ? "animate-pulse" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {row.status === "ready" && row.download_url && (
        <a
          href={row.download_url}
          download
          className="btn-primary mt-3 inline-flex w-full justify-center text-sm"
          data-testid="export-download"
        >
          {t("ui.exportBundleModal.download", { size: prettyBytes(row.byte_size) })}
        </a>
      )}
      {row.status === "ready" && !row.download_url && (
        <p className="mt-2 text-[11px] text-nb-text-3">
          {t("ui.exportBundleModal.noStorageUrl")}
        </p>
      )}
      {row.status === "failed" && (
        <p role="alert" className="mt-2 text-xs text-cvp-danger">
          {row.error ?? t("ui.exportBundleModal.generationFailed")}
        </p>
      )}
    </div>
  );
}


function ExportRowItem({
  row,
  onTrack,
}: {
  row: ExportRow;
  onTrack: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2 rounded-md border border-nb-border bg-nb-surface/30 px-3 py-2 text-xs">
      <span className="flex-1 truncate">
        <span className="font-medium text-nb-text">
          {row.status === "ready"
            ? prettyBytes(row.byte_size)
            : row.status.toUpperCase()}
        </span>
        <span className="ml-2 text-nb-text-3">
          {formatDateTime(row.requested_at)}
        </span>
      </span>
      {row.status === "ready" && row.download_url && (
        <a
          href={row.download_url}
          download
          className="rounded border border-nb-mint/40 bg-nb-mint/10 px-2 py-1 text-nb-mint hover:bg-nb-mint/20"
        >
          {t("ui.exportBundleModal.downloadShort")}
        </a>
      )}
      {(row.status === "queued" || row.status === "processing") && (
        <button
          type="button"
          onClick={onTrack}
          className="rounded border border-nb-border px-2 py-1 text-nb-text-2 hover:bg-nb-surface"
        >
          {t("ui.exportBundleModal.track")}
        </button>
      )}
    </li>
  );
}


function prettyBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
